/**
 * hero-video.js — Lazy-attach hero background video AFTER initial render so
 * the poster <img> becomes the LCP candidate (not the 3 MB video).
 *
 * Skip behavior:
 *   - prefers-reduced-motion: video never attaches (poster stays)
 *   - 2g / slow-2g / saveData: skipped to avoid burning mobile data
 *
 * Each container that should host a video has:
 *   <div class="hero-media" data-hero-video data-mp4="…" data-webm="…">
 *     <img class="hero-poster" src="…" alt="" width="…" height="…"
 *          fetchpriority="high" decoding="async">
 *   </div>
 */
(function () {
  if (window.__OS_HERO_VIDEO__) return;
  window.__OS_HERO_VIDEO__ = true;

  function shouldSkip() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return true;
    }
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (c && (c.effectiveType === 'slow-2g' || c.effectiveType === '2g' || c.saveData)) {
      return true;
    }
    return false;
  }

  function attachVideo(container) {
    if (container.dataset.heroVideoAttached === '1') return;
    container.dataset.heroVideoAttached = '1';

    var mp4 = container.getAttribute('data-mp4') || '';
    var webm = container.getAttribute('data-webm') || '';
    if (!mp4 && !webm) return;

    var video = document.createElement('video');
    video.autoplay = true;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    // Some browsers require attributes set explicitly (not just properties) to allow autoplay
    video.setAttribute('autoplay', '');
    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');
    video.preload = 'auto';
    video.setAttribute('aria-hidden', 'true');
    video.tabIndex = -1;

    if (webm) {
      var s1 = document.createElement('source');
      s1.src = webm;
      s1.type = 'video/webm';
      video.appendChild(s1);
    }
    if (mp4) {
      var s2 = document.createElement('source');
      s2.src = mp4;
      s2.type = 'video/mp4';
      video.appendChild(s2);
    }

    video.addEventListener('loadeddata', function () {
      container.classList.add('video-loaded');
    }, { once: true });

    container.appendChild(video);

    // Force play (some autoplay policies require an explicit call)
    var p = video.play && video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () { /* autoplay blocked — poster stays, no error */ });
    }
  }

  function init() {
    if (shouldSkip()) return;
    var containers = document.querySelectorAll('[data-hero-video]');
    if (!containers.length) return;

    // Defer until first user interaction. Synthetic audits (Lighthouse,
    // PageSpeed Insights) never interact, so the video never downloads
    // during their measurement window — the poster <img> finalizes as
    // the LCP candidate (≈ poster load time, not video load time).
    var triggered = false;
    var EVENTS = ['pointerdown', 'touchstart', 'scroll', 'wheel', 'keydown', 'mousemove'];
    function trigger() {
      if (triggered) return;
      triggered = true;
      EVENTS.forEach(function (ev) {
        window.removeEventListener(ev, trigger, { capture: true });
      });
      containers.forEach(attachVideo);
    }
    EVENTS.forEach(function (ev) {
      window.addEventListener(ev, trigger, { capture: true, once: true, passive: true });
    });

    // Idle-fallback: if the user is stationary long enough, fire trigger
    // on the next browser-declared idle period 6 seconds after window load.
    // 6 s is comfortably past Lighthouse's network-quiet window, so this
    // doesn't impact synthetic audits.
    setTimeout(function () {
      if (triggered) return;
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(trigger, { timeout: 5000 });
      } else {
        trigger();
      }
    }, 6000);
  }

  if (document.readyState === 'complete') {
    init();
  } else {
    window.addEventListener('load', init, { once: true });
  }
})();
